import { FormControl } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { FormikValues, getIn } from "formik";

import { useFieldError } from "../../forms/missing-fields/hooks/useFieldError";
import { getFieldPath } from "../FormikField.utils";
import { useOptionalLabel } from "../useOptionalLabel";
import { FormikDatePickerProps } from "./FormikDatePicker.types";

export function FormikDatePicker<T extends FormikValues>(props: FormikDatePickerProps<T>) {
  const {
    fieldName,
    path,
    formControlProps,
    formik,
    helperText,
    textFieldProps,
    label,
    disabled,
    required,
    optional,
    clearable,
    onClear,
    onValueChange,
    ...others
  } = props;

  const fullPath = getFieldPath(path, fieldName);
  const resolvedLabel = useOptionalLabel(label, optional);
  const error = useFieldError(formik, fullPath, label);

  return (
    <FormControl fullWidth {...formControlProps}>
      <DatePicker
        label={resolvedLabel}
        name={fullPath}
        value={(getIn(formik.values, fullPath) as Date | null | undefined) ?? null}
        onChange={(value: Date | null) => {
          void formik.setFieldValue(fullPath, value, false);
          onValueChange?.(value);
        }}
        disabled={disabled}
        slotProps={{
          field: { clearable, onClear },
          textField: {
            required,
            variant: "outlined",
            size: "small",
            ...textFieldProps,
            error: !!error,
            helperText: error ?? helperText ?? " ",
            inputProps: { required: false },
            InputLabelProps: {
              sx: { fontSize: "0.875rem" },
              required,
            },
            InputProps: {
              sx: {
                paddingLeft: 0,
                "& .MuiPickersSectionList-root": {
                  minHeight: "40px",
                  lineHeight: "24px",
                  padding: "8px 14px",
                  fontSize: "0.875rem",
                  alignItems: "center",
                },
              },
            },
          },
        }}
        {...others}
      />
    </FormControl>
  );
}
